import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, inject } from "@angular/core";
import { Router, ActivatedRoute, NavigationEnd } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { Store } from "@ngrx/store";
import { Observable, Subject, of } from "rxjs";
import { withLatestFrom, map, switchMap, take, takeUntil, tap, filter } from "rxjs/operators";

import { TasksModel } from "../tasks.model";
import { ApplicationState, I18N } from "../../shared/types";
import { DuplicateTaskDialogComponent } from "./duplicate-task-dialog/duplicate-task-dialog.component";

@Component({
	selector: "task-types",
	templateUrl: "task-types.component.html",
	styleUrls: ["task-types.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TaskTypesComponent implements OnInit, OnDestroy {
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly model = inject(TasksModel);
	private readonly store = inject<Store<ApplicationState>>(Store);
	private dialog = inject(MatDialog);

	// @ts-expect-error TS7008
	options$;
	// @ts-expect-error TS2564
	optionName$: Observable<string | I18N>;

	private readonly taskDestroy$ = new Subject<void>();
	private readonly destroy$ = new Subject<void>();

	onTaskSelect(taskOption: any) {
		const navigationPath = [taskOption.code];

		if (!taskOption.children) {
			const residentId = this.route.snapshot.paramMap.get("patient");

			const currentShift$ = this.store.select((state) => state.data.shift).pipe(take(1));
			const residentTasks$ = this.store
				// @ts-expect-error TS2538
				.select((state) => state.data.tasks[residentId] || { items: [] })
				.pipe(
					map((tasks) => tasks.items),
					take(1),
				);
			const residentTasksForShift$ = residentTasks$.pipe(
				switchMap((tasks) =>
					currentShift$.pipe(map((currentShift) => [...tasks].filter((task) => task.shift === currentShift.id))),
				),
				take(1),
			);

			residentTasksForShift$
				.pipe(
					switchMap((tasksForShift) => {
						const existingTaskForCurrentShift = tasksForShift.find((task) => task.code === taskOption.code);

						if (existingTaskForCurrentShift) {
							let message = "tasks.taskForShiftExists";

							if (existingTaskForCurrentShift.executed) {
								message = "tasks.signedTaskForShiftExists";
							}

							const dialogRef = this.dialog.open(DuplicateTaskDialogComponent, {
								data: { message },
							});

							return dialogRef.afterClosed();
						}

						return of(true);
					}),
					takeUntil(this.taskDestroy$),
				)
				.subscribe((confirmed) => {
					if (confirmed) {
						navigationPath.push("create");

						this.router.navigate(navigationPath, { relativeTo: this.route });

						return;
					}

					this.router.navigate([residentId, "overview", "home", "tasks"]);
				});

			return;
		}

		this.router.navigate(navigationPath, { relativeTo: this.route });
	}

	goBack() {
		const { patient, path } = this.route.snapshot.params;

		if (path === "") {
			this.router.navigate([patient, "overview"]);
		} else {
			this.router.navigate([".."], { relativeTo: this.route });
		}
	}

	ngOnInit() {
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				map((event: NavigationEnd) => event.url),
				tap(() => this.taskDestroy$.next()),
				takeUntil(this.destroy$),
			)
			.subscribe();

		this.options$ = this.route.url.pipe(
			withLatestFrom(this.model.caretree$),
			map(([segments, caretree]) => {
				const { path } = segments[segments.length - 1];

				if (path === "task") {
					return caretree;
				}

				const taskParams = segments.slice(1).map((segment) => segment.path);
				let iterator = [...caretree] as any[];

				for (const param of taskParams) {
					iterator = iterator.find((tree) => tree.code === param).children;
				}

				return iterator;
			}),
		);

		this.optionName$ = this.route.url.pipe(
			switchMap((segments) => {
				const path = [];
				for (const segment of segments) {
					path.push(segment.path);
				}

				if (path.length === 1) {
					return of("tasks.task");
				}

				return this.model.getCategoryName(path.slice(1));
			}),
		);
	}

	ngOnDestroy(): void {
		this.taskDestroy$.next();
		this.destroy$.next();
		this.taskDestroy$.complete();
		this.destroy$.complete();
	}
}
